// src/store/api/propertyTypeApi.ts
import {baseApi} from "./baseApi";
import type {
  CreatePropertyTypeDto,
  UpdatePropertyTypeDto,
  PropertyTypeFilterDto,
  PropertyTypeResponseDto,
  PropertyTypeStatsDto,
  PaginatedPropertyTypeResponseDto,
} from "../../types/property-type";

export const propertyTypeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Property Type
    createPropertyType: builder.mutation<
      PropertyTypeResponseDto,
      CreatePropertyTypeDto
    >({
      query: (data) => ({
        url: "/property-types",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PropertyType"],
    }),

    // Get Property Types with filters and pagination
    getPropertyTypes: builder.query<
      PaginatedPropertyTypeResponseDto,
      PropertyTypeFilterDto
    >({
      query: (params) => ({
        url: "/property-types",
        params,
      }),
      transformResponse: (response: {data: PaginatedPropertyTypeResponseDto}) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({id}) => ({
                type: "PropertyType" as const,
                id,
              })),
              {type: "PropertyType", id: "LIST"},
            ]
          : [{type: "PropertyType", id: "LIST"}],
    }),

    // Get Property Type Stats
    getPropertyTypeStats: builder.query<PropertyTypeStatsDto, void>({
      query: () => "/property-types/stats",
      transformResponse: (response: {data: PropertyTypeStatsDto}) => response.data,
      providesTags: ["PropertyType"],
    }),

    // Get Property Type by Slug
    getPropertyTypeBySlug: builder.query<PropertyTypeResponseDto, string>({
      query: (slug) => `/property-types/slug/${slug}`,
      transformResponse: (response: {data: PropertyTypeResponseDto}) => response.data,
      providesTags: (result) =>
        result ? [{type: "PropertyType", id: result.id}] : [],
    }),

    // Get Property Type by ID
    getPropertyTypeById: builder.query<PropertyTypeResponseDto, number>({
      query: (id) => `/property-types/${id}`,
      transformResponse: (response: {data: PropertyTypeResponseDto}) => response.data,
      providesTags: (result, error, id) => [{type: "PropertyType", id}],
    }),

    // Update Property Type
    updatePropertyType: builder.mutation<
      PropertyTypeResponseDto,
      {id: number; data: UpdatePropertyTypeDto}
    >({
      query: ({id, data}) => ({
        url: `/property-types/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "PropertyType", id},
        {type: "PropertyType", id: "LIST"},
      ],
    }),

    // Delete Property Type
    deletePropertyType: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/property-types/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "PropertyType", id},
        {type: "PropertyType", id: "LIST"},
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreatePropertyTypeMutation,
  useGetPropertyTypesQuery,
  useGetPropertyTypeStatsQuery,
  useGetPropertyTypeBySlugQuery,
  useGetPropertyTypeByIdQuery,
  useUpdatePropertyTypeMutation,
  useDeletePropertyTypeMutation,
} = propertyTypeApi;
